<template>
  <div class="school-live">
    <header class="topbar">
      <div class="brand-lockup">
        <div class="brand-mark">EC</div>
        <div>
          <div class="eyebrow">实时训练记录 <span>· SCHOOL OPERATIONS</span></div>
          <h1>{{ schoolTitle }}</h1>
        </div>
      </div>
      <div class="topbar-center">驾校车辆智能调度台</div>
      <div class="top-actions">
        <span class="header-clock">{{ formatTime(serverTime) }}</span>
        <span :class="['mqtt-pill', mqttConnected ? 'ok' : 'bad']">
          <i></i>{{ mqttConnected ? 'MQTT在线' : 'MQTT离线' }}
        </span>
        <button class="ghost-button" @click="loadOnlineCars">刷新数据</button>
      </div>
    </header>

    <section class="stats">
      <div class="stat stat-primary">
        <div class="stat-label"><span class="stat-index">01</span>在线车辆</div>
        <div class="stat-value"><strong>{{ cars.length }}</strong><small>辆</small></div>
        <div class="stat-note">当前接入训练场</div>
      </div>
      <div class="stat">
        <div class="stat-label"><span class="stat-index">02</span>练车学员</div>
        <div class="stat-value"><strong>{{ activeStudentCount }}</strong><small>人</small></div>
        <div class="stat-note">正在进行训练</div>
      </div>
      <div class="stat">
        <div class="stat-label"><span class="stat-index">03</span>视频覆盖</div>
        <div class="stat-value"><strong>{{ videoCount }}</strong><small>辆</small></div>
        <div class="stat-note">已绑定视频设备</div>
      </div>
      <div class="stat stat-speed">
        <div class="stat-label"><span class="stat-index">04</span>最高车速</div>
        <div class="stat-value"><strong>{{ maxSpeed }}</strong><small>km/h</small></div>
        <div class="stat-note">实时信号峰值</div>
      </div>
    </section>

    <main class="layout">
      <section class="map-panel">
        <div class="panel-title">
          <div class="panel-title-main">
            <span class="panel-kicker">LIVE MAP</span>
            <strong>场地车辆分布</strong>
          </div>
          <div class="panel-title-meta">
            <span class="map-status"><i></i>信号同步中</span>
            <span>{{ selectedCar ? selectedCar.carNum : '未选择车辆' }}</span>
          </div>
        </div>
        <canvas
          ref="mapCanvas"
          :class="['map-canvas', { dragging: mapDragging }]"
          tabindex="0"
          role="img"
          aria-label="场地车辆分布地图，可拖动或使用方向键平移"
          @click="handleMapClick"
          @pointerdown="startMapDrag"
          @pointermove="moveMapDrag"
          @pointerup="endMapDrag"
          @pointercancel="endMapDrag"
          @keydown="handleMapKeydown"
        ></canvas>
        <div class="map-controls" @click.stop>
          <button
            class="map-control-button"
            type="button"
            aria-label="缩小场地地图"
            title="缩小"
            :disabled="mapZoom <= 1"
            @click="zoomMap(0.8)"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 12h12" />
            </svg>
          </button>
          <button
            class="map-control-button map-zoom-value"
            type="button"
            aria-label="重置场地地图缩放"
            title="重置缩放"
            @click="resetMapZoom"
          >
            {{ Math.round(mapZoom * 100) }}%
          </button>
          <button
            class="map-control-button"
            type="button"
            aria-label="放大场地地图"
            title="放大"
            :disabled="mapZoom >= 8"
            @click="zoomMap(1.25)"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 6v12M6 12h12" />
            </svg>
          </button>
        </div>
      </section>

      <aside class="side-panel">
        <div class="panel-title">
          <div class="panel-title-main">
            <span class="panel-kicker">FLEET</span>
            <strong>在线车辆</strong>
          </div>
          <div class="panel-title-meta">
            <span>{{ cars.length }} 辆在线</span>
            <span>{{ formatTime(serverTime) }}</span>
          </div>
        </div>
        <div class="car-list">
          <button
            v-for="car in cars"
            :key="car.carId"
            :class="['car-row', selectedCar && selectedCar.carId === car.carId ? 'active' : '']"
            @click="selectCar(car.carId)"
          >
            <span class="car-row-main">
              <span class="plate">{{ car.carNum || car.carIndex || car.carId }}</span>
              <span class="student">{{ car.studentName || '--' }}</span>
            </span>
            <span class="car-row-state"><i></i>在线</span>
            <span class="speed">{{ formatSpeed(getSignal(car.carId).speed) }} <small>km/h</small></span>
          </button>
          <div v-if="!cars.length" class="empty">暂无在线练车车辆</div>
        </div>
      </aside>

      <section class="video-panel">
        <div class="panel-title">
          <div class="panel-title-main">
            <span class="panel-kicker">LIVE FEED</span>
            <strong>车辆视频</strong>
          </div>
          <div class="panel-title-meta">
            <span class="video-device">{{ selectedCar ? selectedCar.videoDevice || '未绑定设备' : '请选择车辆' }}</span>
            <span class="video-state"><i></i>{{ selectedCar && selectedVideoUrl ? '已连接' : '等待连接' }}</span>
          </div>
        </div>
        <SchoolVideoPlayer
          :key="selectedCar ? selectedCar.carId : 'empty'"
          :access-token="selectedCar ? selectedCar.accessToken : ''"
          :video-url="selectedVideoUrl"
        />
      </section>

      <section class="signal-panel">
        <div class="panel-title">
          <div class="panel-title-main">
            <span class="panel-kicker">TELEMETRY</span>
            <strong>车辆信号</strong>
          </div>
          <div class="panel-title-meta">
            <span class="selected-label">{{ selectedCar ? '当前选中' : '等待选车' }}</span>
            <span>{{ selectedSignalTime }}</span>
          </div>
        </div>
        <div class="signal-grid" v-if="selectedCar">
          <div class="signal"><span>车牌号</span><strong>{{ selectedCar.carNum || '--' }}</strong></div>
          <div class="signal"><span>学员</span><strong>{{ selectedCar.studentName || '--' }}</strong></div>
          <div class="signal"><span>车型</span><strong>{{ selectedCar.licenseName || '--' }}</strong></div>
          <div class="signal"><span>科目</span><strong>{{ selectedCar.courseName || '--' }}</strong></div>
          <div class="signal"><span>速度</span><strong>{{ formatSpeed(selectedSignal.speed) }} km/h</strong></div>
          <div class="signal"><span>档位</span><strong>{{ selectedSignal.gear || '-' }}</strong></div>
          <div class="signal"><span>转速</span><strong>{{ selectedSignal.rmp || 0 }} RPM</strong></div>
          <div class="signal"><span>安全带</span><strong>{{ selectedSignal.belt === 1 ? '已系' : '未系' }}</strong></div>
          <div class="signal"><span>左转</span><strong>{{ selectedSignal.leftLight === 1 ? '打开' : '关闭' }}</strong></div>
          <div class="signal"><span>右转</span><strong>{{ selectedSignal.rightLight === 1 ? '打开' : '关闭' }}</strong></div>
          <div class="signal"><span>刹车</span><strong>{{ formatPedalState(selectedSignal.break) }}</strong></div>
          <div class="signal"><span>离合</span><strong>{{ formatPedalState(selectedSignal.cluth) }}</strong></div>
        </div>
        <div v-else class="empty">请从列表或地图选择车辆</div>
      </section>
    </main>
  </div>
</template>

<script>
import axios from 'axios'
import mqtt from 'mqtt'
import { getMap } from '@/utils/map.js'
import SchoolVideoPlayer from './SchoolVideoPlayer.vue'

const SCHOOL_THEME = Object.freeze({
  canvasBackground: '#020d08',
  mapLine: '#FFFFFF',
  carActive: '#4ade80',
  carNormal: '#22c55e',
  labelActive: 'rgba(74, 222, 128, 0.62)',
  labelNormal: 'rgba(220, 252, 231, 0.48)',
  labelBorderActive: '#22c55e',
  labelBorderNormal: 'rgba(20, 83, 45, 0.82)',
  labelText: '#052e16'
})
const DEFAULT_MAP_ZOOM = 4
const TRAJECTORY_BASE_SCALE = 10
const CAR_ROTATION_OFFSET = Math.PI + 100
const CAR_IMAGE_VERTICAL_OFFSET = 14

export default {
  name: 'SchoolLiveDashboard',
  components: { SchoolVideoPlayer },
  data() {
    return {
      cars: [],
      selectedCarId: null,
      mapData: [],
      mapZoom: DEFAULT_MAP_ZOOM,
      mapPan: { x: 0, y: 0 },
      mapDragging: false,
      mapDragged: false,
      suppressMapClick: false,
      mapDragStart: { x: 0, y: 0 },
      mapDragOrigin: { x: 0, y: 0 },
      carImage: null,
      canvasWidth: 0,
      canvasHeight: 0,
      bounds: null,
      signals: {},
      mqttClient: null,
      mqttConnected: false,
      serverTime: Date.now(),
      refreshTimer: null,
      connection: {
        protocol: 'wss',
        host: 'stulive.xihaxueche.com',
        endpoint: '/mqtt',
        username: 'admin',
        password: 'ghgd.123'
      }
    }
  },
  computed: {
    query() {
      return new URLSearchParams(window.location.search)
    },
    apiBase() {
      return this.query.get('apiBase') || '/api'
    },
    schoolId() {
      return this.query.get('schoolId') || this.query.get('schoold') || ''
    },
    schoolTitle() {
      return (this.selectedCar && this.selectedCar.schoolName) || '驾校练车总览'
    },
    selectedCar() {
      return this.cars.find(car => car.carId === this.selectedCarId) || this.cars[0] || null
    },
    selectedSignal() {
      return this.selectedCar ? this.getSignal(this.selectedCar.carId) : {}
    },
    selectedVideoUrl() {
      return this.resolveVideoUrl(this.selectedCar)
    },
    selectedSignalTime() {
      return this.selectedSignal._time ? this.formatTime(this.selectedSignal._time) : '等待信号'
    },
    activeStudentCount() {
      return this.cars.filter(car => car.studentId && car.studentId !== 0).length
    },
    videoCount() {
      return this.cars.filter(car => this.resolveVideoUrl(car) && car.accessToken).length
    },
    maxSpeed() {
      const speeds = this.cars.map(car => Number(this.getSignal(car.carId).speed) || 0)
      return speeds.length ? Math.max(...speeds).toFixed(1) : '0.0'
    }
  },
  watch: {
    selectedCarId() {
      this.drawScene()
    }
  },
  mounted() {
    this.loadCarImage()
    this.mapData = this.parseMapData(getMap())
    this.resizeCanvas()
    this.loadOnlineCars()
    this.connectMqtt()
    this.refreshTimer = setInterval(this.loadOnlineCars, 30000)
    window.addEventListener('resize', this.resizeCanvas)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCanvas)
    clearInterval(this.refreshTimer)
    if (this.mqttClient) this.mqttClient.end()
  },
  methods: {
    loadOnlineCars() {
      axios.get(`${this.apiBase}/main/realtimeTraining/school`, {
        params: this.schoolId ? { schoolId: this.schoolId } : {}
      }).then(res => {
        const data = res.data && res.data.data ? res.data.data : {}
        this.cars = data.cars || []
        this.serverTime = data.serverTime || Date.now()
        if (!this.selectedCarId && this.cars.length) {
          this.selectedCarId = this.cars[0].carId
        }
        this.loadMapFromCars()
        this.drawScene()
      }).catch(() => {
        this.cars = []
        this.drawScene()
      })
    },
    loadMapFromCars() {
      const mapFile = (this.selectedCar && this.selectedCar.mapFile) || (this.cars.find(car => car.mapFile) || {}).mapFile
      if (!mapFile) return
      const mapUrl = this.resolveMapFileUrl(mapFile)
      fetch(mapUrl).then(response => {
        if (!response.ok) throw new Error(`Map request failed: ${response.status}`)
        return response.text()
      }).then(text => {
        const mapData = this.parseMapData(text)
        if (!mapData.length) throw new Error('Map file has no drawable data')
        this.mapData = mapData
        this.bounds = null
        if (!this.centerMapOnCar(this.selectedCarId)) this.drawScene()
      }).catch(() => {})
    },
    resolveMapFileUrl(mapFile) {
      const value = String(mapFile || '').trim()
      if (!value) return ''
      if (/^https?:\/\//i.test(value) || value.startsWith('/')) return value
      if (/^\d+(,\d+)*$/.test(value)) {
        return `${this.apiBase}/system/oss/download/${value.split(',')[0]}`
      }
      return value
    },
    resolveVideoUrl(car) {
      if (!car) return ''

      // trajectory.vue 已验证车辆绑定的 ezopen 地址可以直接播放，优先使用同一条链路。
      const videoDevice = String(car.videoDevice || '').trim()
      if (/^ezopen:\/\//i.test(videoDevice)) return videoDevice

      // 后端返回的 live/address/get 结果可能是 JSON 字符串，作为没有 ezopen 地址时的兜底。
      return this.extractVideoUrl(car.videoUrl)
    },
    extractVideoUrl(value) {
      if (!value) return ''
      if (typeof value === 'string') {
        const text = value.trim()
        if (!text) return ''
        if (/^ezopen:\/\//i.test(text) || /^https?:\/\//i.test(text)) return text
        try {
          return this.extractVideoUrl(JSON.parse(text))
        } catch (e) {
          return ''
        }
      }
      if (Array.isArray(value)) {
        for (const item of value) {
          const url = this.extractVideoUrl(item)
          if (url) return url
        }
        return ''
      }
      if (typeof value !== 'object') return ''
      const candidates = [value.ezopen, value.url, value.liveAddress, value.data, value.result]
      for (const candidate of candidates) {
        const url = this.extractVideoUrl(candidate)
        if (url) return url
      }
      return ''
    },
    connectMqtt() {
      const url = `${this.connection.protocol}://${this.connection.host}${this.connection.endpoint}`
      this.mqttClient = mqtt.connect(url, {
        clean: true,
        connectTimeout: 30000,
        reconnectPeriod: 4000,
        clientId: `school-live-${Date.now()}`,
        username: this.connection.username,
        password: this.connection.password
      })
      this.mqttClient.on('connect', () => {
        this.mqttConnected = true
        this.mqttClient.subscribe('vehicle/+/up/#', { qos: 0 })
      })
      this.mqttClient.on('close', () => {
        this.mqttConnected = false
      })
      this.mqttClient.on('message', (topic, message) => {
        const data = this.parseMessage(message.toString())
        if (!data || !data.carId) return
        this.$set(this.signals, Number(data.carId), { ...data, _time: Date.now() })
        if (Number(data.carId) === Number(this.selectedCarId)) {
          this.centerMapOnCar(data.carId)
        } else {
          this.drawScene()
        }
      })
    },
    parseMessage(payload) {
      try {
        return JSON.parse(payload)
      } catch (e) {
        const parts = payload.split(';')
        if (parts.length < 32) return null
        return {
          carId: Number(parts[0]) || 0,
          studentId: Number(parts[1]) || 0,
          timestamp: Number(parts[2]) || 0,
          x: Number(parts[4]) || 0,
          y: Number(parts[5]) || 0,
          azimuth: Number(parts[10]) || 0,
          speed: Number(parts[13]) || 0,
          cluth: Number(parts[14]) || 0,
          oil: Number(parts[15]) || 0,
          break: Number(parts[16]) || 0,
          handBreak: Number(parts[19]) || 0,
          belt: Number(parts[20]) || 0,
          gear: parts[21] || '-',
          leftLight: Number(parts[22]) || 0,
          rightLight: Number(parts[23]) || 0,
          rmp: parts.length > 32 ? Number(parts[32]) || 0 : 0
        }
      }
    },
    getSignal(carId) {
      return this.signals[Number(carId)] || {}
    },
    formatSpeed(value) {
      const speed = Number(value)
      return Number.isFinite(speed) ? speed.toFixed(1) : '0.0'
    },
    formatPedalState(value) {
      return Number(value) > 0 ? '踩' : '未踩'
    },
    selectCar(carId) {
      this.selectedCarId = carId
      this.$nextTick(() => {
        this.centerMapOnCar(carId)
      })
    },
    centerMapOnCar(carId) {
      const signal = this.getSignal(carId)
      if (signal.x === undefined || signal.y === undefined) return false
      const bounds = this.bounds || this.computeBounds()
      if (!bounds) return false
      this.bounds = bounds
      const point = this.projectPoint(signal.y, signal.x, bounds)
      this.mapPan = this.clampMapPan(
        this.mapPan.x + this.canvasWidth / 2 - point.x,
        this.mapPan.y + this.canvasHeight / 2 - point.y
      )
      this.drawScene()
      return true
    },
    loadCarImage() {
      const img = new Image()
      img.src = require('@/assets/car.png')
      img.onload = () => {
        this.carImage = img
        this.drawScene()
      }
    },
    resizeCanvas() {
      const canvas = this.$refs.mapCanvas
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      this.canvasWidth = rect.width
      this.canvasHeight = rect.height
      canvas.width = rect.width
      canvas.height = rect.height
      this.mapPan = this.clampMapPan(this.mapPan.x, this.mapPan.y)
      if (!this.centerMapOnCar(this.selectedCarId)) this.drawScene()
    },
    parseMapData(text) {
      const groups = {}
      text.split('\n').filter(line => line.trim()).forEach(line => {
        const parts = line.split(',')
        if (parts.length < 5) return
        const head = parts[0]
        const lng = Number(parts[2])
        const lat = Number(parts[1])
        const endFlag = parts[parts.length - 1].trim()
        let key = endFlag
        if (/^1300[1-4]/.test(endFlag)) key = `${endFlag}_${head}`
        if (!groups[key]) groups[key] = []
        groups[key].push([lng, lat])
      })
      const validPrefixes = ['11', '12', '13', '14', '15']
      return Object.keys(groups)
        .filter(key => validPrefixes.some(prefix => key.startsWith(prefix)))
        .map(key => ({
          key,
          projectType: key.slice(0, 2),
          points: groups[key]
        }))
    },
    computeBounds() {
      let minX = Infinity
      let maxX = -Infinity
      let minY = Infinity
      let maxY = -Infinity
      this.mapData.forEach(item => item.points.forEach(([x, y]) => {
        minX = Math.min(minX, x)
        maxX = Math.max(maxX, x)
        minY = Math.min(minY, y)
        maxY = Math.max(maxY, y)
      }))
      if (!Number.isFinite(minX)) {
        this.cars.forEach(car => {
          const signal = this.getSignal(car.carId)
          if (signal.x === undefined || signal.y === undefined) return
          minX = Math.min(minX, signal.y)
          maxX = Math.max(maxX, signal.y)
          minY = Math.min(minY, signal.x)
          maxY = Math.max(maxY, signal.x)
        })
      }
      if (!Number.isFinite(minX)) return null
      return { minX, maxX, minY, maxY }
    },
    getProjectionScale() {
      return TRAJECTORY_BASE_SCALE * (this.mapZoom / DEFAULT_MAP_ZOOM)
    },
    zoomMap(factor) {
      const nextZoom = Math.max(1, Math.min(8, this.mapZoom * factor))
      this.mapZoom = Number(nextZoom.toFixed(2))
      this.mapPan = this.clampMapPan(this.mapPan.x, this.mapPan.y)
      if (!this.centerMapOnCar(this.selectedCarId)) this.drawScene()
    },
    resetMapZoom() {
      this.mapZoom = DEFAULT_MAP_ZOOM
      this.mapPan = { x: 0, y: 0 }
      if (!this.centerMapOnCar(this.selectedCarId)) this.drawScene()
    },
    getMapPanLimits(bounds) {
      if (!bounds || !this.canvasWidth || !this.canvasHeight) return { x: 0, y: 0 }
      const padding = 24
      const width = Math.max(bounds.maxX - bounds.minX, 1)
      const height = Math.max(bounds.maxY - bounds.minY, 1)
      const scale = this.getProjectionScale(bounds)
      const availableWidth = Math.max(this.canvasWidth - padding * 2, 0)
      const availableHeight = Math.max(this.canvasHeight - padding * 2, 0)
      const renderedWidth = width * scale
      const renderedHeight = height * scale
      return {
        x: Math.max(Math.abs(availableWidth - renderedWidth) / 2, renderedWidth / 2),
        y: Math.max(Math.abs(availableHeight - renderedHeight) / 2, renderedHeight / 2)
      }
    },
    clampMapPan(x, y) {
      const bounds = this.bounds || this.computeBounds()
      const limits = this.getMapPanLimits(bounds)
      return {
        x: Math.max(-limits.x, Math.min(limits.x, x)),
        y: Math.max(-limits.y, Math.min(limits.y, y))
      }
    },
    startMapDrag(event) {
      if (event.button !== undefined && event.button !== 0) return
      this.mapDragging = true
      this.mapDragged = false
      this.suppressMapClick = false
      this.mapDragStart = { x: event.clientX, y: event.clientY }
      this.mapDragOrigin = { x: this.mapPan.x, y: this.mapPan.y }
      if (event.currentTarget.setPointerCapture) {
        event.currentTarget.setPointerCapture(event.pointerId)
      }
    },
    moveMapDrag(event) {
      if (!this.mapDragging) return
      const deltaX = event.clientX - this.mapDragStart.x
      const deltaY = event.clientY - this.mapDragStart.y
      if (Math.hypot(deltaX, deltaY) > 3) this.mapDragged = true
      this.mapPan = this.clampMapPan(
        this.mapDragOrigin.x + deltaX,
        this.mapDragOrigin.y + deltaY
      )
      this.drawScene()
    },
    endMapDrag(event) {
      if (!this.mapDragging) return
      if (
        event.currentTarget.hasPointerCapture &&
        event.currentTarget.hasPointerCapture(event.pointerId)
      ) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
      this.mapDragging = false
      this.suppressMapClick = this.mapDragged
      if (this.suppressMapClick) {
        setTimeout(() => {
          this.suppressMapClick = false
        }, 0)
      }
    },
    handleMapKeydown(event) {
      const step = 24
      const offsets = {
        ArrowLeft: [-step, 0],
        ArrowRight: [step, 0],
        ArrowUp: [0, -step],
        ArrowDown: [0, step]
      }
      const offset = offsets[event.key]
      if (!offset) return
      event.preventDefault()
      this.mapPan = this.clampMapPan(
        this.mapPan.x + offset[0],
        this.mapPan.y + offset[1]
      )
      this.drawScene()
    },
    projectPoint(x, y, bounds) {
      const padding = 24
      const width = Math.max(bounds.maxX - bounds.minX, 1)
      const height = Math.max(bounds.maxY - bounds.minY, 1)
      const scale = this.getProjectionScale(bounds)
      return {
        x: padding + (x - bounds.minX) * scale + (this.canvasWidth - padding * 2 - width * scale) / 2 + this.mapPan.x,
        y: padding + (y - bounds.minY) * scale + (this.canvasHeight - padding * 2 - height * scale) / 2 + this.mapPan.y
      }
    },
    drawScene() {
      const canvas = this.$refs.mapCanvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight)
      ctx.fillStyle = SCHOOL_THEME.canvasBackground
      ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight)
      const bounds = this.computeBounds()
      this.bounds = bounds
      if (!bounds) return
      this.drawMap(ctx, bounds)
      this.drawCars(ctx, bounds)
    },
    drawMap(ctx, bounds) {
      ctx.strokeStyle = SCHOOL_THEME.mapLine
      ctx.lineWidth = 3
      const scale = this.getProjectionScale(bounds)
      this.mapData.forEach(item => {
        const poly = item.points
        if (poly.length < 3) return
        ctx.beginPath()
        const firstPoint = this.projectPoint(poly[0][0], poly[0][1], bounds)
        ctx.moveTo(firstPoint.x, firstPoint.y)
        for (let index = 1; index < poly.length; index++) {
          const previous = poly[index - 1]
          const current = poly[index]
          const point = this.projectPoint(current[0], current[1], bounds)
          const threshold = 500 / scale
          if (
            Math.abs(current[0] - previous[0]) > threshold ||
            Math.abs(current[1] - previous[1]) > threshold
          ) {
            ctx.moveTo(point.x, point.y)
          } else {
            ctx.lineTo(point.x, point.y)
          }
        }
        if (this.shouldCloseMapItem(item)) ctx.closePath()
        ctx.stroke()
      })
    },
    shouldCloseMapItem(item) {
      return item.points.length >= 3 && item.points.length <= 30
    },
    drawCars(ctx, bounds) {
      const relativeZoom = this.mapZoom / DEFAULT_MAP_ZOOM
      const carWidth = 25 * relativeZoom
      const carHeight = 45 * relativeZoom
      const labelHeight = 18
      const labelTopOffset = carHeight / 2 + 2
      this.cars.forEach(car => {
        const signal = this.getSignal(car.carId)
        const hasPosition = signal.x !== undefined && signal.y !== undefined
        const point = hasPosition
          ? this.projectPoint(signal.y, signal.x, bounds)
          : this.projectPoint((bounds.minX + bounds.maxX) / 2, (bounds.minY + bounds.maxY) / 2, bounds)
        const active = this.selectedCar && this.selectedCar.carId === car.carId
        const carAngle = this.normalizeAngle((Number(signal.azimuth) || 0) - 55)
        ctx.save()
        ctx.translate(point.x, point.y)
        ctx.rotate(carAngle * Math.PI / 180 + CAR_ROTATION_OFFSET)
        if (this.carImage) {
          ctx.drawImage(
            this.carImage,
            -carWidth / 2,
            -carHeight / 2 - CAR_IMAGE_VERTICAL_OFFSET * relativeZoom,
            carWidth,
            carHeight
          )
        } else {
          ctx.fillStyle = active ? SCHOOL_THEME.carActive : SCHOOL_THEME.carNormal
          ctx.fillRect(-carWidth / 2, -carHeight / 2, carWidth, carHeight)
        }
        ctx.restore()
        ctx.fillStyle = active ? SCHOOL_THEME.labelActive : SCHOOL_THEME.labelNormal
        ctx.strokeStyle = active ? SCHOOL_THEME.labelBorderActive : SCHOOL_THEME.labelBorderNormal
        ctx.lineWidth = active ? 2 : 1
        ctx.font = '600 11px Arial'
        ctx.textAlign = 'center'
        const text = car.carNum || String(car.carId)
        const width = ctx.measureText(text).width + 12
        ctx.fillRect(point.x - width / 2, point.y + labelTopOffset, width, labelHeight)
        ctx.strokeRect(point.x - width / 2, point.y + labelTopOffset, width, labelHeight)
        ctx.fillStyle = SCHOOL_THEME.labelText
        ctx.fillText(text, point.x, point.y + labelTopOffset + 13)
      })
    },
    normalizeAngle(angle) {
      const normalized = angle % 360.0
      if (Math.abs(normalized - 360.0) < 1e-10) {
        return 0.0
      }
      return normalized < 0 ? normalized + 360.0 : normalized
    },
    handleMapClick(event) {
      if (this.suppressMapClick) {
        this.suppressMapClick = false
        return
      }
      if (!this.bounds) return
      const rect = this.$refs.mapCanvas.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top
      let nearest = null
      let distance = Infinity
      this.cars.forEach(car => {
        const signal = this.getSignal(car.carId)
        if (signal.x === undefined || signal.y === undefined) return
        const point = this.projectPoint(signal.y, signal.x, this.bounds)
        const currentDistance = Math.hypot(point.x - x, point.y - y)
        if (currentDistance < distance) {
          distance = currentDistance
          nearest = car
        }
      })
      if (nearest && distance < 42) this.selectCar(nearest.carId)
    },
    formatTime(time) {
      if (!time) return '--'
      const date = new Date(time)
      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.school-live {
  --school-bg: #020b07;
  --school-bg-deep: #010604;
  --school-surface: rgba(5, 22, 14, 0.92);
  --school-surface-raised: rgba(9, 34, 22, 0.78);
  --school-surface-active: rgba(10, 65, 37, 0.9);
  --school-accent: #23c987;
  --school-accent-bright: #73f0b0;
  --school-accent-soft: #a8f4c7;
  --school-text: #f0fff7;
  --school-text-secondary: #91b8a4;
  --school-border: rgba(73, 218, 143, 0.22);
  --school-border-strong: rgba(115, 240, 176, 0.7);
  --school-glow: rgba(35, 201, 135, 0.2);
  --school-danger: #f6a1a1;
  width: 100vw;
  height: 100vh;
  min-height: 100vh;
  overflow: hidden;
  box-sizing: border-box;
  padding: 20px 24px 22px;
  display: flex;
  flex-direction: column;
  gap: 0;
  color: var(--school-text);
  background:
    linear-gradient(135deg, rgba(11, 58, 37, 0.16), transparent 36%),
    var(--school-bg);
  font-family: 'Inter', 'SF Pro Display', Arial, 'Microsoft YaHei', sans-serif;
}

.topbar,
.stats,
.layout {
  width: 100%;
  box-sizing: border-box;
}

.topbar {
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  border-bottom: 1px solid var(--school-border);
}

.brand-lockup,
.top-actions,
.panel-title-meta,
.panel-title-main,
.stat-label,
.stat-value,
.car-row-main,
.car-row-state,
.mqtt-pill,
.map-status,
.video-state {
  display: flex;
  align-items: center;
}

.brand-lockup {
  min-width: 320px;
  gap: 14px;
}

.brand-mark {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #04170c;
  background: var(--school-accent-bright);
  border: 1px solid rgba(240, 255, 247, 0.7);
  border-radius: 12px;
  box-shadow: 0 0 0 5px rgba(35, 201, 135, 0.08), 0 12px 28px rgba(35, 201, 135, 0.18);
  font-size: 13px;
  font-weight: 900;
  letter-spacing: -0.5px;
}

.eyebrow {
  color: var(--school-accent-bright);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
}

.eyebrow span {
  color: var(--school-text-secondary);
  font-size: 9px;
  letter-spacing: 1px;
}

h1 {
  margin: 5px 0 0;
  color: var(--school-text);
  font-size: 28px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.8px;
}

.topbar-center {
  margin-left: auto;
  color: var(--school-text-secondary);
  font-size: 12px;
  letter-spacing: 3px;
  text-transform: uppercase;
}

.top-actions {
  flex: 0 0 auto;
  gap: 10px;
}

.header-clock {
  color: var(--school-text-secondary);
  font: 600 12px/1 'SF Mono', Consolas, monospace;
  letter-spacing: 0.5px;
}

.ghost-button {
  min-width: 88px;
  height: 36px;
  padding: 0 14px;
  color: var(--school-accent-soft);
  background: transparent;
  border: 1px solid var(--school-border-strong);
  border-radius: 7px;
  cursor: pointer;
  font: 700 12px/1 'Inter', Arial, 'Microsoft YaHei', sans-serif;
  transition: color 180ms ease, border-color 180ms ease, background-color 180ms ease, box-shadow 180ms ease;
}

.ghost-button:hover,
.ghost-button:focus-visible {
  color: var(--school-text);
  background: var(--school-surface-active);
  border-color: var(--school-accent-bright);
  box-shadow: 0 0 16px var(--school-glow);
  outline: none;
}

.mqtt-pill {
  gap: 7px;
  min-height: 30px;
  padding: 0 10px;
  color: var(--school-text-secondary);
  background: rgba(5, 27, 17, 0.6);
  border: 1px solid var(--school-border);
  border-radius: 7px;
  font-size: 11px;
}

.mqtt-pill i,
.map-status i,
.video-state i,
.car-row-state i {
  width: 6px;
  height: 6px;
  display: inline-block;
  flex: 0 0 6px;
  border-radius: 50%;
  background: var(--school-accent);
  box-shadow: 0 0 0 3px rgba(35, 201, 135, 0.11);
}

.mqtt-pill.ok,
.video-state {
  color: var(--school-accent-soft);
}

.mqtt-pill.bad {
  color: var(--school-danger);
}

.mqtt-pill.bad i {
  background: var(--school-danger);
  box-shadow: 0 0 0 3px rgba(246, 161, 161, 0.12);
}

.stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin: 8px 0 10px;
}

.stat,
.map-panel,
.side-panel,
.video-panel,
.signal-panel {
  background: var(--school-surface);
  border: 1px solid var(--school-border);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.14), inset 0 1px 0 rgba(240, 255, 247, 0.035);
}

.stat {
  position: relative;
  min-height: 58px;
  padding: 8px 14px 7px;
  overflow: hidden;
  border-radius: 11px;
}

.stat::after {
  position: absolute;
  top: 0;
  right: 0;
  width: 36%;
  height: 1px;
  background: var(--school-accent);
  content: '';
  opacity: 0.5;
}

.stat-primary {
  border-color: rgba(115, 240, 176, 0.42);
}

.stat-label {
  gap: 8px;
  color: var(--school-text-secondary);
  font-size: 11px;
  font-weight: 600;
}

.stat-index {
  color: var(--school-accent);
  font: 700 10px/1 'SF Mono', Consolas, monospace;
}

.stat-value {
  gap: 8px;
  margin-top: 4px;
}

.stat strong {
  color: var(--school-text);
  font: 800 22px/1 'SF Mono', Consolas, monospace;
  letter-spacing: -1px;
}

.stat small {
  color: var(--school-accent-soft);
  font-size: 12px;
  font-weight: 700;
}

.stat-note {
  margin-top: 3px;
  color: var(--school-text-secondary);
  font-size: 9px;
}

.layout {
  display: grid;
  grid-template-columns: minmax(360px, 1fr) minmax(0, 2fr) 340px;
  grid-template-rows: minmax(0, 1fr) 286px;
  grid-template-areas:
    'map map cars'
    'video signals signals';
  gap: 12px;
  flex: 1;
  min-height: 0;
}

.map-panel,
.side-panel,
.video-panel,
.signal-panel {
  min-width: 0;
  overflow: hidden;
  border-radius: 11px;
}

.map-panel {
  position: relative;
  grid-area: map;
  min-height: 0;
}

.side-panel {
  grid-area: cars;
}

.video-panel {
  grid-area: video;
  display: grid;
  grid-template-rows: 62px minmax(0, 1fr);
}

.signal-panel {
  grid-area: signals;
}

.panel-title {
  height: 62px;
  padding: 0 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid var(--school-border);
  box-sizing: border-box;
}

.panel-title-main {
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.panel-title-main strong {
  color: var(--school-text);
  font-size: 14px;
  font-weight: 800;
}

.panel-kicker {
  color: var(--school-accent);
  font: 700 9px/1 'SF Mono', Consolas, monospace;
  letter-spacing: 1.5px;
}

.panel-title-meta {
  min-width: 0;
  justify-content: flex-end;
  gap: 12px;
  color: var(--school-text-secondary);
  font: 600 11px/1 'SF Mono', Consolas, monospace;
  white-space: nowrap;
}

.map-status,
.video-state {
  gap: 7px;
  font: 700 10px/1 'Inter', Arial, sans-serif;
}

.map-canvas {
  display: block;
  width: 100%;
  height: calc(100% - 62px);
  cursor: grab;
  touch-action: none;
  user-select: none;
}

.map-canvas.dragging {
  cursor: grabbing;
}

.map-canvas:focus-visible {
  outline: 2px solid var(--school-accent-bright);
  outline-offset: -3px;
}

.map-controls {
  position: absolute;
  top: 76px;
  right: 16px;
  z-index: 2;
  display: flex;
  gap: 6px;
  padding: 5px;
  background: rgba(2, 11, 7, 0.78);
  border: 1px solid rgba(115, 240, 176, 0.32);
  border-radius: 9px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.26);
  backdrop-filter: blur(8px);
}

.map-control-button {
  width: 38px;
  height: 34px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--school-accent-soft);
  background: transparent;
  border: 0;
  border-radius: 5px;
  cursor: pointer;
  transition: color 180ms ease, background-color 180ms ease, box-shadow 180ms ease;
}

.map-control-button svg {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
}

.map-control-button:hover:not(:disabled),
.map-control-button:focus-visible {
  color: var(--school-text);
  background: rgba(35, 201, 135, 0.22);
  box-shadow: inset 0 0 0 1px rgba(115, 240, 176, 0.35);
  outline: none;
}

.map-control-button:disabled {
  opacity: 0.42;
  cursor: not-allowed;
}

.map-zoom-value {
  width: 54px;
  color: var(--school-text-secondary);
  font: 700 10px/1 'SF Mono', Consolas, monospace;
}

.car-list {
  height: calc(100% - 62px);
  overflow: auto;
  padding: 12px;
  box-sizing: border-box;
}

.car-row {
  position: relative;
  width: 100%;
  min-height: 66px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: 1fr 1fr;
  align-items: center;
  column-gap: 10px;
  margin-bottom: 8px;
  padding: 10px 12px 9px 14px;
  color: var(--school-text);
  text-align: left;
  background: rgba(9, 34, 22, 0.7);
  border: 1px solid rgba(73, 218, 143, 0.16);
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease;
}

.car-row::before {
  position: absolute;
  top: 12px;
  bottom: 12px;
  left: 0;
  width: 2px;
  background: transparent;
  border-radius: 2px;
  content: '';
}

.car-row:hover,
.car-row:focus-visible {
  background: rgba(15, 58, 35, 0.82);
  border-color: rgba(115, 240, 176, 0.42);
  outline: none;
  transform: translateX(-2px);
}

.car-row.active {
  background: rgba(10, 72, 41, 0.92);
  border-color: var(--school-border-strong);
  box-shadow: inset 3px 0 0 var(--school-accent-bright), 0 0 22px rgba(35, 201, 135, 0.12);
}

.car-row.active::before {
  background: var(--school-accent-bright);
}

.car-row-main {
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
}

.plate {
  color: var(--school-text);
  font: 800 13px/1 'SF Mono', Consolas, monospace;
}

.student {
  overflow: hidden;
  max-width: 120px;
  color: var(--school-text-secondary);
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.car-row-state {
  gap: 6px;
  align-self: start;
  color: var(--school-accent-soft);
  font-size: 10px;
}

.speed {
  grid-column: 2;
  grid-row: 2;
  color: var(--school-accent-soft);
  font: 700 11px/1 'SF Mono', Consolas, monospace;
}

.speed small {
  color: var(--school-text-secondary);
  font-size: 9px;
}

.video-device {
  max-width: 270px;
  overflow: hidden;
  color: var(--school-text-secondary);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.video-panel >>> .school-video-player {
  border: 0;
  border-radius: 0 0 10px 10px;
}

.signal-grid {
  height: calc(100% - 62px);
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  padding: 12px;
  box-sizing: border-box;
}

.signal {
  min-height: 64px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-sizing: border-box;
  background: rgba(9, 34, 22, 0.7);
  border: 1px solid rgba(73, 218, 143, 0.16);
  border-radius: 7px;
}

.signal span {
  color: var(--school-text-secondary);
  font-size: 10px;
}

.signal strong {
  display: block;
  overflow: hidden;
  margin-top: 8px;
  color: var(--school-text);
  font: 800 15px/1 'SF Mono', Consolas, monospace;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty {
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--school-text-secondary);
  font-size: 12px;
}

button:focus-visible {
  outline: 2px solid var(--school-accent-bright);
  outline-offset: 2px;
}

@media screen and (max-width: 1200px) {
  .school-live {
    padding-left: 18px;
    padding-right: 18px;
  }

  .topbar-center {
    display: none;
  }

  .layout {
    grid-template-columns: minmax(0, 1fr) 300px;
    grid-template-areas:
      'map cars'
      'video signals';
  }
}

@media screen and (max-width: 960px) {
  .school-live {
    height: auto;
    min-height: 100vh;
    overflow: auto;
  }

  .stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .layout {
    display: flex;
    flex-direction: column;
    flex: none;
    height: auto;
  }

  .map-panel {
    height: 460px;
  }

  .side-panel {
    height: 320px;
  }

  .video-panel {
    height: 300px;
  }

  .signal-panel {
    min-height: 300px;
  }
}

@media screen and (max-width: 620px) {
  .school-live {
    padding: 14px;
  }

  .topbar {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
    padding-bottom: 14px;
  }

  .brand-lockup {
    min-width: 0;
  }

  .top-actions {
    width: 100%;
    justify-content: space-between;
  }

  .header-clock {
    display: none;
  }

  .stats {
    gap: 8px;
    margin-top: 12px;
  }

  .stat {
    min-height: 92px;
    padding: 12px;
  }

  .stat strong {
    font-size: 22px;
  }

  .panel-title {
    padding-left: 14px;
    padding-right: 14px;
  }

  .panel-title-meta > span:first-child {
    display: none;
  }

  .signal-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
